import React, { useState } from "react";

import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import type {
  AddIngredientPayload,
  FetchedRecipe,
  MessageResponse,
  OverlayState,
  PaginatedBomItems,
  RecipeFormValues,
  RecipeLineItem,
  RecipeMutationResponse,
} from "../../features/recipes/recipeTypes";
import {
  emptyRecipeForm,
  recipeToFormValues,
} from "../../features/recipes/recipeTypes";
import { makePostApiCall } from "../../shared/makeApiCall";
import { generateUniqueNumber } from "../../shared/utility";

const emptyOverlay: OverlayState = { childComponent: null, open: false };

interface ApiCallResult<T> {
  success?: boolean;
  data?: T;
  errorStatus?: number;
}

interface RecipeEditorOptions {
  navigateOnCreate?: boolean;
  startsLoading?: boolean;
}

export function useRecipeEditorViewModel(
  logout: () => void,
  addSnackbar: (snackbar: SnackbarData) => void,
  options?: RecipeEditorOptions,
) {
  const [emptyModal, setEmptyModelOpen] = useState<OverlayState>(emptyOverlay);
  const [bomItemsLoading, setBomItemsLoading] = useState(false);
  const [bomItems, setBomItems] = useState<PaginatedBomItems>({ data: [] });
  const [recipeItems, setRecipeItems] = useState<RecipeLineItem[]>([]);
  const [packagingItems, setPackagingItems] = useState<RecipeLineItem[]>([]);
  const [recipe, setRecipe] = useState<RecipeFormValues>(emptyRecipeForm);
  const [recipeLoading, setRecipeLoading] = useState(
    Boolean(options?.startsLoading),
  );
  const [navigateDestination, setNavigateDestination] = useState<string | null>(
    null,
  );

  const getRecipe = async (recipeId: string) => {
    setRecipeLoading(true);
    const result = (await makePostApiCall(
      `fetchRecipe/${recipeId}`,
    )) as ApiCallResult<FetchedRecipe>;
    setRecipeLoading(false);
    if (result.success && result.data) {
      setRecipe(recipeToFormValues(result.data.recipe));
      setRecipeItems(result.data.recipeItems ?? []);
      setPackagingItems(result.data.packagingItems ?? []);
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const getBomItems = async (search: string, page: number) => {
    setBomItemsLoading(true);
    const result = (await makePostApiCall("myIngredients", {
      page,
      search,
    })) as ApiCallResult<PaginatedBomItems>;
    setBomItemsLoading(false);
    if (result.success && result.data) {
      setBomItems(result.data);
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const addBomToIngredient = async (payload: AddIngredientPayload) => {
    const result = (await makePostApiCall(
      "addIngredientToRecipe",
      payload,
    )) as ApiCallResult<RecipeLineItem>;
    setBomItemsLoading(false);
    if (result.success) {
      return result;
    }
    if (result.errorStatus === 401) {
      logout();
    }
    return result;
  };

  const saveRecipe = async (values: RecipeFormValues, isEdit: boolean) => {
    const updatePayload = {
      recipe: values,
      recipeItems,
      packagingItems,
    };
    const result = (await makePostApiCall(
      isEdit ? "updateRecipe" : "createRecipe",
      isEdit ? updatePayload : values,
    )) as ApiCallResult<RecipeMutationResponse>;

    if (result.success && result.data?.recipe) {
      const savedRecipe = recipeToFormValues(result.data.recipe);
      setRecipe(savedRecipe);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          result.data.message,
          SnackbarType.Success,
        ),
      );
      const createdId = savedRecipe.id;
      if (options?.navigateOnCreate && !isEdit && createdId > 0) {
        setNavigateDestination(`/recipes/${createdId}`);
      }
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const deleteRecipeIngredient = async (id: number, type: number) => {
    const result = (await makePostApiCall(
      `deleteRecipeIngredient/${id}`,
    )) as ApiCallResult<MessageResponse>;
    if (result.success) {
      if (type === 1) {
        setPackagingItems((current) =>
          current.filter((item) => item.id !== id),
        );
      } else {
        setRecipeItems((current) => current.filter((item) => item.id !== id));
      }
      setEmptyModelOpen(emptyOverlay);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          result.data?.message ?? "Ingredient deleted",
          SnackbarType.Success,
        ),
      );
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const askDeleteRecipe = (line: RecipeLineItem, type: number) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteRecipeIngredient(line.id, type)}
          message={`Are you sure you want to delete ${line.bom_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return {
    addBomToIngredient,
    askDeleteRecipe,
    bomItems,
    bomItemsLoading,
    emptyModal,
    getBomItems,
    getRecipe,
    navigateDestination,
    packagingItems,
    recipe,
    recipeItems,
    recipeLoading,
    saveRecipe,
    setEmptyModelOpen,
    setPackagingItems,
    setRecipeItems,
  };
}
